// ============================================================================
// Safe Care - Validation des demandes de suspension (côté validateur)
// Module : features/assistance_sociale/pages
// Route  : /suspension/valide
// Flux   : tableau des DEMANDESUSPENSION EN_ATTENTE + justificatifs joints
//          (DOCUMENT GED, RefDoc='SUSPENSION') -> Valider (-> souscription
//          SUSPENDUE) ou Rejeter (motif obligatoire, souscription reste ACTIVE).
//          Maker != checker : le demandeur ne peut pas valider sa propre demande.
// Charte : .tab / .formTitle / .bG / .clientP / .tabItem (identique à
//          /prets/simulation) — les actions sont sur la barre .bG.
// ============================================================================

import { useState, useMemo } from 'react';
import { Table, Spin, Button, Form, Space, Tabs, Modal, Input } from 'antd';
import { EyeOutlined, CheckCircleOutlined, FilePdfOutlined } from '@ant-design/icons';
import type { TabsProps } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import dayjs from 'dayjs';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import {
  useGetDemandesSuspension,
  useGetDocumentsDemandeSuspension,
  useValiderDemandeSuspension,
  useRejeterDemandeSuspension,
} from '../hooks';
import { DemandeSuspension, DocumentSuspension } from '../types';

const SuspensionValidationPage = () => {
  const auth = useAuthUser();

  const getUserCode = () => {
    const u = auth();
    const raw = u?.coduti ?? (u as any)?.CodUti ?? (u as any)?.CODUTI ?? localStorage.getItem('codUti') ?? sessionStorage.getItem('codUti') ?? '';
    const cleaned = String(raw).trim();
    if (!cleaned || cleaned.toLowerCase() === 'undefined' || cleaned.toLowerCase() === 'null') return '';
    return cleaned;
  };

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDemandeKeys, setSelectedDemandeKeys] = useState<React.Key[]>([]);
  const [modalRejet, setModalRejet] = useState(false);
  const [motifRejet, setMotifRejet] = useState('');
  const [previewDoc, setPreviewDoc] = useState<DocumentSuspension | null>(null);

  const { data: demandesSuspension, isLoading: loadingDemandes, refetch } = useGetDemandesSuspension('EN_ATTENTE', 'SUSPENSION');
  const valider = useValiderDemandeSuspension();
  const rejeter = useRejeterDemandeSuspension();

  const dataDemandes = demandesSuspension ?? [];
  const selectedDemande = dataDemandes.find((d) => d.IdDemandeSuspension === selectedDemandeKeys[0]);
  const { data: docsRes, isLoading: loadingDocs } = useGetDocumentsDemandeSuspension(selectedDemande?.IdDemandeSuspension);
  const documents: DocumentSuspension[] = docsRes ?? [];
  const peutTraiter = !!selectedDemande && selectedDemande.Statut === 'EN_ATTENTE';

  const openValider = () => {
    if (!peutTraiter) return;
    const codeUtilisateur = getUserCode();
    if (!codeUtilisateur) {
      SweetAlert.fire({ title: 'Session invalide', text: 'Impossible de récupérer le code utilisateur.', icon: 'error' });
      return;
    }
    if ((selectedDemande?.UtiSai ?? '').trim().toUpperCase() === codeUtilisateur.toUpperCase()) {
      SweetAlert.fire({ title: 'Validation impossible', text: 'Vous ne pouvez pas valider une demande que vous avez vous-même enregistrée.', icon: 'warning' });
      return;
    }
    SweetAlert.fire({
      title: `Valider la suspension de la souscription #${selectedDemande?.IdSouscription} ?`,
      text: 'La souscription passera au statut SUSPENDUE.',
      showCancelButton: true,
      icon: 'question',
      confirmButtonText: 'Valider',
      cancelButtonText: 'Annuler',
    }).then((r) => {
      if (!r.isConfirmed || !selectedDemande) return;
      setLoading(true);
      valider.mutate(
        { idDemandeSuspension: selectedDemande.IdDemandeSuspension!, utiVal: codeUtilisateur },
        {
          onSuccess: (res) => {
            setLoading(false);
            if (res?.StatutCode === 200) {
              SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
              setSelectedDemandeKeys([]);
            } else {
              SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
            }
          },
          onError: () => setLoading(false),
        }
      );
    });
  };

  const openRejeter = () => {
    if (!peutTraiter) return;
    setMotifRejet('');
    setModalRejet(true);
  };

  const confirmRejet = () => {
    if (!selectedDemande) return;
    if (!motifRejet.trim()) {
      SweetAlert.fire({ title: 'Motif de rejet obligatoire', icon: 'warning' });
      return;
    }
    setLoading(true);
    rejeter.mutate(
      { idDemandeSuspension: selectedDemande.IdDemandeSuspension!, motifRejet, utiVal: getUserCode() },
      {
        onSuccess: (res) => {
          setLoading(false);
          setModalRejet(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
            setSelectedDemandeKeys([]);
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: () => setLoading(false),
      }
    );
  };

  // Détermine si un document est un PDF (PieceJointe = data URI)
  const isPdf = (d: DocumentSuspension) => (d.PieceJointe ?? '').toLowerCase().startsWith('data:application/pdf');
  // Nom d'affichage : la description contient « label | nom de fichier »
  const nomFichier = (d: DocumentSuspension) => d.Description?.split('|').pop()?.trim() || d.NumDoc || 'Document';

  const filteredDemandes = useMemo(() => {
    if (!searchTerm.trim()) return dataDemandes;
    const term = searchTerm.toLowerCase();
    return dataDemandes.filter((d) =>
      [
        d.Numcli,
        d.NOMCLI,
        d.PRECLI,
        d.NomProduit,
        d.Motif,
        d.UtiSai,
      ]
        .map((v) => String(v ?? '').toLowerCase())
        .some((v) => v.includes(term))
    );
  }, [dataDemandes, searchTerm]);

  // Même affichage que la page cotisations : colonnes souples, sans largeurs fixes
  const columns: ColumnsType<DemandeSuspension> = [
    { title: 'N°', dataIndex: 'IdDemandeSuspension', key: 'IdDemandeSuspension' },
    { title: 'Souscription', dataIndex: 'IdSouscription', key: 'IdSouscription' },
    { title: 'Client', dataIndex: 'Numcli', key: 'Numcli' },
    { title: 'Nom', dataIndex: 'NOMCLI', key: 'NOMCLI' },
    { title: 'Prénom', dataIndex: 'PRECLI', key: 'PRECLI' },
    { title: 'Produit', dataIndex: 'NomProduit', key: 'NomProduit' },
    { title: 'Version', dataIndex: 'NumeroVersion', key: 'NumeroVersion' },
    { title: 'Motif', dataIndex: 'Motif', key: 'Motif', ellipsis: true },
    { title: 'Docs', dataIndex: 'NbDocuments', key: 'NbDocuments' },
    {
      title: 'Date demande',
      dataIndex: 'DatCre',
      key: 'DatCre',
      render: (v: string) => (v ? dayjs(v).format('DD/MM/YYYY') : ''),
    },
    { title: 'Saisi par', dataIndex: 'UtiSai', key: 'UtiSai' },
  ];

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <span id="Tab"><CheckCircleOutlined /> Validation de suspension</span>,
      children: null,
    },
  ];

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>SAFE CARE - VALIDATION DES SUSPENSIONS</b>
      </Typography>
      <Spin spinning={loadingDemandes || loading} size="large" style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign="left" form={form} style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!peutTraiter} onClick={openValider}>
                  Valider
                </Button>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!peutTraiter} onClick={openRejeter}>
                  Rejeter
                </Button>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block onClick={() => refetch()}>
                  Actualiser
                </Button>
                <Button size="large" className="bt_add" type="ghost" htmlType="reset" block onClick={() => { setSelectedDemandeKeys([]); setSearchTerm(''); }}>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <div>
              <Tabs size="small" type="card" defaultActiveKey="1" items={items} animated={{ inkBar: true, tabPane: true }} />
            </div>
          </div>
        </Form>
        <div className="tabItem" style={{ display: 'block' }}>
          <div style={{ width: '85%', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}>
            <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
              <legend>Demandes de suspension en attente ({filteredDemandes.length})</legend>
              <Input.Search
                placeholder="Rechercher une demande..."
                allowClear
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ marginBottom: 8, width: 300 }}
              />
              <Table
                size="small"
                rowKey="IdDemandeSuspension"
                rowSelection={{
                  type: 'radio',
                  selectedRowKeys: selectedDemandeKeys,
                  onChange: setSelectedDemandeKeys,
                  onSelect: (record, selected) => {
                    // Permet de désélectionner une ligne déjà sélectionnée
                    if (!selected) setSelectedDemandeKeys([]);
                  },
                }}
                onRow={(record) => ({
                  onClick: () => {
                    // Clic sur la ligne : bascule sélection/désélection
                    const id = (record as any).IdDemandeSuspension;
                    if (selectedDemandeKeys[0] === id) {
                      setSelectedDemandeKeys([]);
                    } else {
                      setSelectedDemandeKeys([id]);
                    }
                  },
                  style: { cursor: 'pointer' },
                })}
                columns={columns}
                dataSource={filteredDemandes}
                scroll={{ x: 10 }}
                pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
              />
            </Box>

            {/* Panneau des justificatifs — même affichage que le tableau des
                échéances de la page cotisations (sous le tableau principal) */}
            {selectedDemande ? (
              <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                <legend>
                  Justificatifs soumis — Demande #{selectedDemande.IdDemandeSuspension} ({selectedDemande.NOMCLI} {selectedDemande.PRECLI})
                </legend>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Souscription : <b>#{selectedDemande.IdSouscription}</b> — Produit : <b>{selectedDemande.NomProduit}</b> (v{selectedDemande.NumeroVersion})
                  {selectedDemande.Motif ? <> — Motif : {selectedDemande.Motif}</> : null}
                </Typography>
                <Spin spinning={loadingDocs} size="large" style={{ color: '#116599' }}>
                  {documents.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: 13 }}>
                      Aucun justificatif soumis pour cette demande
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                      {documents.map((d, i) => {
                        const pdf = isPdf(d);
                        const fichier = nomFichier(d);
                        return (
                          <div
                            key={d.IdDoc ?? i}
                            onClick={() => setPreviewDoc(d)}
                            style={{
                              width: 120, minHeight: 130, borderRadius: 10,
                              border: '1px solid var(--colorOpaque030)',
                              background: '#fff', cursor: 'pointer',
                              display: 'flex', flexDirection: 'column',
                              alignItems: 'center', justifyContent: 'center',
                              position: 'relative', overflow: 'hidden',
                              boxShadow: '0 2px 8px rgba(13,74,112, 0.08)',
                              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 16px rgba(13,74,112, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,74,112, 0.08)';
                            }}
                          >
                            <div style={{
                              width: '100%', padding: '4px 6px',
                              background: d.LibelleDocument ? 'var(--lightColor002)' : 'var(--colorOpaque020)',
                              color: 'var(--darkColor)',
                              fontSize: 10, fontWeight: 600, textAlign: 'center',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              borderBottom: '1px solid var(--colorOpaque030)',
                            }} title={d.LibelleDocument ?? 'Document libre'}>
                              {d.LibelleDocument ?? 'Document libre'}
                            </div>
                            {pdf ? (
                              <>
                                <FilePdfOutlined style={{ fontSize: 36, color: '#ff4d4f' }} />
                                <div style={{ fontSize: 10, padding: '4px 6px', textAlign: 'center', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 }}>
                                  {fichier}
                                </div>
                              </>
                            ) : d.PieceJointe ? (
                              <>
                                <img
                                  src={d.PieceJointe}
                                  alt={fichier}
                                  style={{ width: '100%', height: 78, objectFit: 'cover' }}
                                />
                                <div style={{ fontSize: 10, padding: '4px 6px', textAlign: 'center', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 }}>
                                  {fichier}
                                </div>
                              </>
                            ) : (
                              <div style={{ fontSize: 11, color: '#999' }}>Aperçu indisponible</div>
                            )}
                            <div style={{
                              position: 'absolute', top: 4, right: 4,
                              width: 22, height: 22, borderRadius: '50%',
                              background: 'rgba(17,101,153, 0.85)', color: '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11,
                            }}>
                              <EyeOutlined />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Spin>
              </Box>
            ) : (
              <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2, textAlign: 'center' }}>
                <Box sx={{
                  py: 4, px: 2,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--lightColor002)',
                    color: 'var(--darkColor)',
                    fontSize: 28,
                  }}>
                    ☝
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: 'var(--darkblue)', fontSize: 15 }}>
                    Sélectionnez une demande de suspension pour afficher ses justificatifs
                  </Typography>
                  <Typography sx={{ color: 'var(--colorOpaque060)', fontSize: 13 }}>
                    Choisissez une demande en attente dans le tableau ci-dessus pour visualiser les documents soumis puis valider ou rejeter.
                  </Typography>
                </Box>
              </Box>
            )}
          </div>
        </div>
      </Spin>

      {/* Modale de prévisualisation d'un document — centrée, au-dessus de tout */}
      <Modal
        open={!!previewDoc}
        onCancel={() => setPreviewDoc(null)}
        footer={null}
        width="90%"
        centered
        zIndex={2100}
        className="safeCareModal"
        style={{ maxWidth: '1400px' }}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto', padding: 16 }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--darkblue)' }}>
            <EyeOutlined style={{ color: 'var(--darkColor)' }} />
            {previewDoc ? nomFichier(previewDoc) : 'Aperçu'}
          </div>
        }
      >
        {previewDoc && (
          <div style={{ textAlign: 'center' }}>
            {isPdf(previewDoc) && previewDoc.PieceJointe ? (
              <iframe
                src={previewDoc.PieceJointe}
                title={nomFichier(previewDoc)}
                style={{ width: '100%', height: '75vh', border: '1px solid var(--colorOpaque030)', borderRadius: 10 }}
              />
            ) : previewDoc.PieceJointe ? (
              <img
                src={previewDoc.PieceJointe}
                alt={nomFichier(previewDoc)}
                style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 10, border: '1px solid var(--colorOpaque030)' }}
              />
            ) : (
              <div style={{ padding: 40, color: '#999' }}>Aperçu indisponible</div>
            )}
            {previewDoc.Description && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>{previewDoc.Description}</div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={`Rejeter la demande #${selectedDemande?.IdDemandeSuspension ?? ''}`}
        open={modalRejet}
        onOk={confirmRejet}
        onCancel={() => setModalRejet(false)}
        okText="Rejeter"
        cancelText="Annuler"
        okButtonProps={{ danger: true, loading }}
        centered
        zIndex={1400}
        className="safeCareModal"
      >
        <Form layout="vertical">
          <Form.Item label="Motif du rejet" required>
            <Input.TextArea rows={3} value={motifRejet} onChange={(e) => setMotifRejet(e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SuspensionValidationPage;
